import GuestRegistrationModal from '@/components/GuestRegistrationModal';
import useUser from '@/hooks/useUser';
import { Button, Grid, Text } from '@simplywallst/ui-core';
import BellRingingSolid from '@simplywallst/ui-core/icons/BellRingingSolid';
import { useCallback, useState } from 'react';
import { useReduxStateForScreener } from '@/pages/Screener/hooks/useReduxStateForScreener';
import { useTracking } from 'react-tracking';
import { getScreenerName } from '@/pages/Screener/utils';
import { useGridView } from '@/hooks/useGridView';
import { useSnackbar } from '@/features/snackbar';
import { EditAndSaveScreenerModal } from '@/pages/Screener/components/EditAndSaveScreenerModal';
import EmailAlerts from './images/email-alerts.svg';

type Props = {
  rules: string;
};

export const StockAlertSubscription = ({ rules }: Props) => {
  const { data: user } = useUser();
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const isLoggedIn = user.loggedIn;
  const { trackEvent } = useTracking();

  const { industryName, demonymCountry, navigateTo } =
    useReduxStateForScreener();
  const { addSnackbar, removeSnackbar } = useSnackbar();

  const {
    data: { name, description },
    creationStatus,
    createGrid,
  } = useGridView({
    id: '0',
    onCreate: ({ id, slug }) => {
      setShowSaveModal(false);

      addSnackbar({
        id: 'stock-alert-subscription-screener',
        type: 'positive',
        message:
          'Screener successfully saved. You will receive alerts when we detect new stocks.',
        actionLabel: 'View My Screener',
        actionCallback: () => navigateTo(id, slug),
        secondaryActionLabel: 'Dismiss',
        secondaryActionCallback: () =>
          removeSnackbar({ id: 'stock-alert-subscription-screener' }),
        layout: 'stack',
      });
    },
  });

  const screenerName = getScreenerName(name, demonymCountry, industryName);
  const defaultSaveName = `Copy of ${screenerName}`;

  const handleClick = useCallback(() => {
    if (!isLoggedIn) {
      return setShowRegistrationModal(true);
    }
    setShowSaveModal(true);
  }, [isLoggedIn]);

  const isCreating = creationStatus === 'pending';
  return (
    <>
      <Button styleType="louder" alignIcon="left" onClick={handleClick}>
        <BellRingingSolid />
        Get New Stock Alert
      </Button>

      {showSaveModal && (
        <EditAndSaveScreenerModal
          title="Save screener to receive alerts"
          onClose={() => setShowSaveModal(false)}
          onConfirm={(viewName, viewDescription) => {
            if (!rules) return;
            trackEvent({
              action: 'click',
              subject: 'save_confirm',
            });
            createGrid({
              rules,
              name: viewName,
              description: viewDescription,
            });
          }}
          oldName={defaultSaveName}
          oldDescription={description}
          confirmLabel="Save and Get Alerts"
          secondaryLabel="Cancel"
          disabled={isCreating}
          primaryLoading={isCreating}
        >
          <Grid
            templateColumns="40px 1fr"
            gap="8px"
            alignItems="flex-end"
            height="100%"
          >
            <EmailAlerts />
            <Text color="darkGray" typography="xSmall">
              Receive email updates as soon as new companies fit the criteria of
              a screener with maximum 150 results.
            </Text>
          </Grid>
        </EditAndSaveScreenerModal>
      )}
      {showRegistrationModal && (
        <GuestRegistrationModal
          canClose={true}
          removeWindowScroll={true}
          onClose={() => setShowRegistrationModal(false)}
        />
      )}
    </>
  );
};
